<template>
    <section class="p-80-15 d-flex f-d-c gap80">
        <header>
            <label>Additional Info</label>
            <h2 class="max-w-648">Enter your details and get access to all the goodies</h2>
        </header>
        
        <div class="container formWrapper shadow-box">
            <form
                id="frontpageQuote"
                @focusin.once="captcha_wanted = true"
                @pointerdown.once="captcha_wanted = true"
                :action="home_url + 'quotes/submit/new' + (loadedInIframe ? '?ref_ga=popup' : '')"
                novalidate="novalidate"
                method="post"
                enctype="multipart/form-data"
            >
            <input v-if="ref_ads" type="hidden" v-model="ref_ads" name="ref_ads" />
            <input v-if="ref_from" type="hidden" v-model="ref_from" name="ref_from" />
            <input type="hidden" v-model="recaptcha_token" name="recaptcha_token" />
            <input type="hidden" :value="generated_message" name="message" />
            
            <p class="q-required-legend"><span class="q-required" aria-hidden="true">*</span> Required field</p>

            <div class="f-cols">
                <div class="f-col">
                    <div class="q-input-line">
                        <label for="first-name">First Name <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <input type="text" id="first-name" aria-required="true" name="first_name" v-model="state.first_name" placeholder="First Name" :class="fieldClass('first_name')" />
                            <div v-if="v$.first_name.$error" class="q-error-badge">{{ v$.first_name.$errors[0].$message }}</div>
                        </div>
                        <small class="info">The name we'll use when we reply to your quote.</small>
                    </div>

                    <div class="q-input-line">
                        <label for="last-name">Last Name <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <input type="text" id="last-name" aria-required="true" name="last_name" v-model="state.last_name" placeholder="Last Name" :class="fieldClass('last_name')" />
                            <div v-if="v$.last_name.$error" class="q-error-badge">{{ v$.last_name.$errors[0].$message }}</div>
                        </div>
                        <small class="info">Your family name, as it should appear on the quote.</small>
                    </div>

                    <div class="q-input-line">
                        <label for="email">Email <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <input type="email" id="email" aria-required="true" name="email" v-model="state.email" placeholder="E-mail" :class="fieldClass('email')" :readonly="logged_in" />
                            <div v-if="v$.email.$error" class="q-error-badge">{{ v$.email.$errors[0].$message }}</div>
                        </div>
                        <small class="info">We'll send the quote here; it's also your login if you create an account.</small>
                    </div>

                    <div class="q-input-line">
                        <label>Country <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field q-country" :class="{ 'has-country': state.country }">
                            <vue-select
                                v-model="state.country"
                                :options="support_countries"
                                label-by="name"
                                :close-on-select="true"
                                :searchable="true"
                                :clear-on-select="true"
                                :class="fieldClass('country')"
                                @focus="country_list_used = true"
                            >
                                <template #dropdown-item="{ option }">
                                    <div>
                                        <!-- the list renders every country while closed: no flags until it is used -->
                                        <CountryFlag v-if="country_list_used" :country="option.flag2" />
                                        {{ option.name }}
                                    </div>
                                </template>
                            </vue-select>
                            <span v-if="state.country" class="q-country-flag" aria-hidden="true">
                                <CountryFlag :country="state.country.flag2" />
                            </span>
                            <div v-if="v$.country.$error" class="q-error-badge">{{ v$.country.$errors[0].$message }}</div>
                        </div>
                        <small class="info">Where your company is registered, used for licensing and invoicing.</small>
                        <input type="hidden" name="country" :value="selected_country" />
                    </div>

                    <div class="q-input-line">
                        <label for="company">Company <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <input type="text" name="company" id="company" aria-required="true" v-model="state.company" placeholder="Company" autocomplete="off" :class="fieldClass('company')" />
                            <div v-if="v$.company.$error" class="q-error-badge">{{ v$.company.$errors[0].$message }}</div>
                        </div>
                        <small class="info">The company or organization the license will be issued to.</small>
                    </div>


                    <div class="q-input-line">
                        <label for="descArea">Description <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <textarea id="descArea" aria-required="true" name="description" placeholder="General project info" rows="10" v-model="state.description" :class="fieldClass('description')"></textarea>
                            <div v-if="v$.description.$error" class="q-error-badge">{{ v$.description.$errors[0].$message }}</div>
                        </div>
                        <small class="info">What you scan, in which environment, and any special requirements.</small>
                    </div>
                </div>

                <div class="f-col">
                    <div class="q-input-line">
                        <label for="number_of_apps">Number of apps <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <input type="number" id="number_of_apps" aria-required="true" name="number_of_apps" v-model="state.number_of_apps" placeholder="Ex: 1" :class="fieldClass('number_of_apps')" />
                            <div v-if="v$.number_of_apps.$error" class="q-error-badge">{{ v$.number_of_apps.$errors[0].$message }}</div>
                        </div>
                        <small class="info">How many apps will integrate the barcode scanning SDK.</small>
                    </div>

                    <div class="q-input-line">
                        <label for="number_of_devices">Number of devices <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <input type="number" id="number_of_devices" aria-required="true" name="number_of_devices" v-model="state.number_of_devices" placeholder="Ex: 50" :class="fieldClass('number_of_devices')" />
                            <div v-if="v$.number_of_devices.$error" class="q-error-badge">{{ v$.number_of_devices.$errors[0].$message }}</div>
                        </div>
                        <small class="info">How many devices will use your app's scanning within a year.</small>
                    </div>

                    <div class="q-input-line">
                        <label>Type of app <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field" :class="{ invalid: v$.type_of_app.$error }">
                            <div class="d-flex f-wrap-w gap20">
                                <div class="checkSwitch radio">
                                    <input id="app_type_ent" type="radio" v-model="state.type_of_app" value="Enterprise" name="type_of_app[]" />
                                    <label for="app_type_ent">
                                        Enterprise
                                        <div class="custom-tooltip">
                                            <svg viewBox="0 0 24 25"><use :xlink:href="spriteHref('info')"></use></svg>
                                            <span class="tooltiptext">Any professional app for B2B, B2E, B2G or even B2C use that requires paid or other kind of controlled access, distributed either publicly or privately.</span>
                                        </div>
                                    </label>
                                </div>
                                <div class="checkSwitch radio">
                                    <input id="app_type_con" type="radio" v-model="state.type_of_app" value="Consumer" name="type_of_app[]" />
                                    <label for="app_type_con">
                                        Consumer
                                        <div class="custom-tooltip">
                                            <svg viewBox="0 0 24 25"><use :xlink:href="spriteHref('info')"></use></svg>
                                            <span class="tooltiptext">Any B2C app for personal use, freely &amp; publicly distributed to individual consumers.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            <div v-if="v$.type_of_app.$error" class="q-error-badge">{{ v$.type_of_app.$errors[0].$message }}</div>
                        </div>
                        <small class="info">Enterprise: paid or controlled-access apps. Consumer: free public apps.</small>
                    </div>

                    <div class="q-input-line">
                        <label>Access restrictions <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field" :class="{ invalid: v$.access_restrictions.$error }">
                            <div class="d-flex f-wrap-w gap20">
                                <div class="checkSwitch radio">
                                    <input id="access_restrictions_yes" type="radio" v-model="state.access_restrictions" value="Yes" name="access_restrictions" />
                                    <label for="access_restrictions_yes">Yes</label>
                                </div>
                                <div class="checkSwitch radio">
                                    <input id="access_restrictions_no" type="radio" v-model="state.access_restrictions" value="No" name="access_restrictions" />
                                    <label for="access_restrictions_no">No</label>
                                </div>
                            </div>
                            <div v-if="v$.access_restrictions.$error" class="q-error-badge">{{ v$.access_restrictions.$errors[0].$message }}</div>
                        </div>
                        <small class="info">Prepaid logins, subscriptions or other kinds of controlled access?</small>
                    </div>

                    <div class="q-input-line">
                        <label>How did you hear about us? <span class="q-required" aria-hidden="true">*</span></label>
                        <div class="q-field">
                            <vue-select
                                v-model="state.heard_about_us"
                                :options="heard_about_us_options"
                                :close-on-select="true"
                                :clear-on-select="true"
                                :class="fieldClass('heard_about_us')"
                            >
                                <template #label="{ selected }">
                                    <input type="text" readonly tabindex="-1" class="q-select-label" :class="{ 'has-value': selected }" :placeholder="selected || 'Select option'" />
                                </template>
                            </vue-select>
                            <div v-if="v$.heard_about_us.$error" class="q-error-badge">{{ v$.heard_about_us.$errors[0].$message }}</div>
                        </div>
                        <small class="info">Helps us understand where our customers find us.</small>
                        <input type="hidden" name="heard_about_us" :value="state.heard_about_us" />
                    </div>

                </div>
            </div>

            <section v-if="!logged_in" class="q-option-box" :class="{ open: state.create_acc }">
                <div class="checkSwitch q-option-toggle">
                    <input id="create_acc" type="checkbox" v-model="state.create_acc" name="create_acc" />
                    <label for="create_acc">
                        <span class="q-option-title">Also create an account for me</span>
                        <span class="q-option-desc">You will need an account to access the SDK download section and the trial/production license generators.</span>
                    </label>
                </div>

                <div v-if="state.create_acc" class="q-option-body">
                    <div class="q-option-col">
                        <div class="q-input-line">
                            <label for="password">Password <span class="q-required" aria-hidden="true">*</span></label>
                            <div class="q-field">
                                <input type="password" name="password" id="password" aria-required="true" v-model="state.password" placeholder="Password" autocomplete="new-password" :class="fieldClass('password')" />
                                <div v-if="v$.password.$error" class="q-error-badge">{{ v$.password.$errors[0].$message }}</div>
                            </div>
                        </div>
                        <div class="q-input-line">
                            <label for="confirm_password">Confirm Password <span class="q-required" aria-hidden="true">*</span></label>
                            <div class="q-field">
                                <input type="password" name="confirm_password" id="confirm_password" aria-required="true" v-model="state.confirm_password" placeholder="Confirm Password" autocomplete="new-password" :class="{ invalid: v$.confirm_password.$error || passwordMismatch }" />
                                <div v-if="v$.confirm_password.$error" class="q-error-badge">{{ v$.confirm_password.$errors[0].$message }}</div>
                            </div>
                            <div v-if="confirmPasswordHasInput" class="password-validation">
                                <div class="item" :class="{ valid: passwordsMatch }">
                                    {{ passwordMismatch ? "Passwords do not match" : "Passwords match" }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="q-option-col">
                        <div class="q-input-line">
                            <span class="q-input-label">Password requirements</span>
                            <div class="password-validation">
                                <div class="item" :class="{ valid: passwordHasLength }">Between 8 and 20 characters long</div>
                                <div class="item" :class="{ valid: passwordHasUpper }">One uppercase character</div>
                                <div class="item" :class="{ valid: passwordHasLower }">One lowercase character</div>
                                <div class="item" :class="{ valid: passwordHasDigitOrSpecial }">One number, whitespace or special character</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="!logged_in && state.create_acc" class="q-option-box" :class="{ open: state.create_license }">
                <div class="checkSwitch q-option-toggle">
                    <input id="create_license" type="checkbox" v-model="state.create_license" name="create_license" />
                    <label for="create_license">
                        <span class="q-option-title">Create a trial license for the selected platforms</span>
                        <span class="q-option-desc">The license is bound to the app IDs of the platforms you selected above.</span>
                    </label>
                </div>

                <div v-if="state.create_license" class="q-option-body">
                    <p v-if="!has_trial_targets" class="q-option-note" :class="{ invalid: v$.create_license?.$error }">
                        Select Android, iOS, Linux, WebAssembly or a cross-platform framework above to choose what the trial license is bound to.
                    </p>

                    <div v-if="needs_android || needs_ios" class="q-option-col">
                        <div v-if="needs_android" class="q-input-line">
                            <label for="app_name">{{ shared_mobile_id ? 'App ID' : 'Android application ID' }} <span class="q-required" aria-hidden="true">*</span></label>
                            <div class="q-field q-has-icon" :class="{ 'q-has-two-icons': shared_mobile_id }">
                                <span class="q-input-icon" aria-hidden="true">
                                    <svg viewBox="0 0 20 11.27"><use :xlink:href="spriteHref('android_logo')"></use></svg>
                                    <svg v-if="shared_mobile_id" viewBox="0 0 16.28 20"><use :xlink:href="spriteHref('ios_logo')"></use></svg>
                                </span>
                                <input type="text" name="app_name" id="app_name" v-model="state.app_name" placeholder="com.company.app" autocomplete="off" aria-required="true" :class="fieldClass('app_name')" />
                                <div v-if="v$.app_name?.$error" class="q-error-badge">{{ v$.app_name.$errors[0].$message }}</div>
                            </div>
                            <small v-if="shared_mobile_id" class="info">Your app identifier (iOS: "bundle identifier" / Android: "application ID"). The license will be bound to it on both platforms.</small>
                            <small v-else class="info">The applicationId from your Android app's build.gradle.</small>
                        </div>

                        <div v-if="needs_ios && !shared_mobile_id" class="q-input-line">
                            <label for="app_name_ios">iOS bundle identifier <span class="q-required" aria-hidden="true">*</span></label>
                            <div class="q-field q-has-icon">
                                <span class="q-input-icon" aria-hidden="true">
                                    <svg viewBox="0 0 16.28 20"><use :xlink:href="spriteHref('ios_logo')"></use></svg>
                                </span>
                                <input type="text" name="app_name_ios" id="app_name_ios" v-model="state.app_name_ios" placeholder="com.company.app.ios" autocomplete="off" aria-required="true" :class="fieldClass('app_name_ios')" />
                                <div v-if="v$.app_name_ios?.$error" class="q-error-badge">{{ v$.app_name_ios.$errors[0].$message }}</div>
                            </div>
                            <small class="info">The bundle identifier from your Xcode project.</small>
                        </div>

                        <div v-if="needs_android && needs_ios" class="q-input-line">
                            <div class="checkSwitch">
                                <input id="separate_app_ids" type="checkbox" v-model="state.separate_app_ids" />
                                <label for="separate_app_ids">My Android and iOS apps use different IDs</label>
                            </div>
                        </div>
                    </div>

                    <div v-if="needs_linux" class="q-option-col">
                        <div class="q-input-line">
                            <label for="app_name_linux">Linux app name <span class="q-required" aria-hidden="true">*</span></label>
                            <div class="q-field q-has-icon">
                                <span class="q-input-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24"><use :xlink:href="spriteHref('linux_logo')"></use></svg>
                                </span>
                                <input type="text" name="app_name_linux" id="app_name_linux" v-model="state.app_name_linux" placeholder="myscanner" maxlength="15" autocomplete="off" aria-required="true" :class="fieldClass('app_name_linux')" />
                                <div v-if="v$.app_name_linux?.$error" class="q-error-badge">{{ v$.app_name_linux.$errors[0].$message }}</div>
                            </div>
                            <small class="info">The process name of your Linux application, 15 characters or fewer.</small>
                        </div>
                    </div>

                    <div v-if="wasm_selected" class="q-option-col">
                        <div class="q-input-line">
                            <label for="domain">WASM domain <span class="q-required" aria-hidden="true">*</span></label>
                            <div class="q-field q-has-icon">
                                <span class="q-input-icon" aria-hidden="true">
                                    <svg viewBox="0 0 48 48"><use :xlink:href="spriteHref('wasm_logo')"></use></svg>
                                </span>
                                <input type="text" name="domain" id="domain" v-model="state.domain" placeholder="example.com" autocomplete="off" aria-required="true" :class="fieldClass('domain')" />
                                <div v-if="v$.domain?.$error" class="q-error-badge">{{ v$.domain.$errors[0].$message }}</div>
                            </div>
                            <small class="info">The domain where the WASM SDK will be used; localhost is included for development.</small>
                        </div>
                    </div>
                </div>
            </section>

            <div class="q-form-actions">
                <div class="d-flex f-d-c f-ai-r">
                    <div class="q-field q-captcha" :class="{ invalid: v$.recaptcha_token.$error }" style="min-height: 78px">
                        <!-- reCAPTCHA loads once the visitor starts on the form, not with the page (22 October 2026: it
                             cost every page with this form about 350 KB and the main thread on phones) -->
                        <RecaptchaV2
                            v-if="captcha_wanted"
                            class="captcha"
                            @widget-id="handleWidgetId"
                            @error-callback="handleErrorCalback"
                            @expired-callback="handleExpiredCallback"
                            @load-callback="handleLoadCallback"
                        />
                        <div v-if="v$.recaptcha_token.$error" class="q-error-badge">Please check the reCAPTCHA box</div>
                    </div>
                </div>
                <div v-if="form_submitting" class="q-input-line submitting">
                    Submitting...
                    <div class="loadingBars">
                        <div class="rect1"></div>
                        <div class="rect2"></div>
                        <div class="rect3"></div>
                        <div class="rect4"></div>
                        <div class="rect5"></div>
                    </div>
                </div>
                <div v-else class="d-flex f-j-e">
                    <button type="submit" class="button link red arrow-right" @click.prevent="submitForm">
                        Submit
                        <svg viewBox="0 0 20 20"><use :xlink:href="spriteHref('arrow_right')"></use></svg>
                    </button>
                </div>
            </div>
            </form>
</div>
</section>
</template>

<script>
import { ref, reactive, computed, defineAsyncComponent } from "vue";
import useValidate from "@vuelidate/core";
import { required, requiredIf, integer, email, helpers } from "@vuelidate/validators";
import VueNextSelect from "vue-next-select";
// import "vue-next-select/dist/index.min.css";
// the flags (230 KB) load when one is shown: the country list is opened, or a signed-in person's country is filled in
const CountryFlag = defineAsyncComponent(() => import("vue-country-flag-next"));
import { HOME_URL, ENV, WEB_USERNAME, WEB_PASS } from "../constants/envrionment";
import { RecaptchaV2, useRecaptcha } from "vue3-recaptcha-v2";

import * as general_config from "../assets/general_config";

const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
const CROSS_PLATFORM_FRAMEWORKS = ["Flutter", "React Native", "Capacitor", "Cordova", ".NET Maui", "NativeScript"];

/**
 * Which trial license IDs the selected platforms and frameworks need.
 * Cross-platform mobile frameworks need both the Android and the iOS ID.
 *
 * @param {string[]} technologies Selected platform and framework names.
 * @returns {{android: boolean, ios: boolean, linux: boolean, wasm: boolean}}
 */
function trialTargets(technologies) {
    const cross_platform = technologies.some((t) => CROSS_PLATFORM_FRAMEWORKS.includes(t));
    
    return {
        android: cross_platform || technologies.includes("Android"),
        ios: cross_platform || technologies.includes("iOS"),
        linux: technologies.includes("Linux"),
        wasm: technologies.includes("WebAssembly"),
    };
}

const PLATFORM_NAMES = ["iOS", "Android", "WebAssembly", "Linux", "Windows (DLL)"];

export default {
    components: {
        "vue-select": VueNextSelect,
        CountryFlag,
        RecaptchaV2,
    },
    props: ["logged_in", "selected_barcodes", "selected_technologies"],
    setup(props) {
        const state = reactive({
            recaptcha_token: "",
            first_name: "",
            last_name: "",
            email: "",
            description: "",
            number_of_apps: 1,
            password: "",
            confirm_password: "",
            app_name: "",
            app_name_ios: "",
            app_name_linux: "",
            separate_app_ids: false,
            domain: "",
            type_of_app: "",
            country: "",
            company: "",
            heard_about_us: "",
            access_restrictions: "No",
            number_of_devices: 50,
            message: "",
            create_acc: false,
            create_license: false,
            plan: "",
        });
        
        const rules = computed(() => {
            let account_rules = {},
            required_rules = {
                recaptcha_token: {
                    required: helpers.withMessage("You must prove that you are not a robot - check the reCAPTCHA box.",required),
                },
                first_name: {
                    required: helpers.withMessage("The First Name is required", required),
                },
                last_name: {
                    required: helpers.withMessage("The Last Name is required", required),
                },
                description: {
                    required: helpers.withMessage("The Description field is required", required),
                },
                message: {
                    required: helpers.withMessage("Select at least one Platform, Framework or Symbology",required),
                },
                access_restrictions: {
                    required: helpers.withMessage("The Access Restrictions field is required",required),
                },
                type_of_app: {
                    required: helpers.withMessage("Choose the type of app",required),
                },
                number_of_apps: {
                    required: helpers.withMessage("The Number of apps field is required",required),
                    integer: helpers.withMessage("Whole numbers only",integer),
                },
                number_of_devices: {
                    required: helpers.withMessage("The Number of devices field is required",required),
                    integer: helpers.withMessage("Whole numbers only",integer),
                },
                email: {
                    required: helpers.withMessage("The E-mail field is required", required),
                    email: helpers.withMessage("Enter a valid email address",email),
                },
                company: {
                    required: helpers.withMessage("The Company field is required", required),
                },
                country: {
                    required: helpers.withMessage("The Country field is required", required),
                },
                heard_about_us: {
                    required: helpers.withMessage("Tell us how you heard about us", required),
                },
            };
            
            if (!props.logged_in && state.create_acc) {
                account_rules = {
                    password: {
                        required: helpers.withMessage("Password is required", required),
                        length: helpers.withMessage("Password must be between 8 and 20 characters", (val) => !val || (String(val).length >= 8 && String(val).length <= 20)),
                        upper: helpers.withMessage("Include at least one uppercase character", (val) => !val || /[A-Z]/.test(String(val))),
                        lower: helpers.withMessage("Include at least one lowercase character", (val) => !val || /[a-z]/.test(String(val))),
                        digitOrSpec: helpers.withMessage("Include at least one number, whitespace or special character", (val) => !val || /(\d|\W|\s)/.test(String(val))),
                    },
                    confirm_password: {
                        required: helpers.withMessage("Please confirm your password", required),
                        sameAs: helpers.withMessage("Passwords don't match", (val) => String(val || "") === String(state.password || "")),
                    },
                };
                
                if (state.create_license) {
                    const targets = trialTargets(props.selected_technologies);
                    const shared_mobile_id = targets.android && targets.ios && !state.separate_app_ids;
                    
                    account_rules["create_license"] = {
                        hasTargets: helpers.withMessage(
                            "Select a platform or framework for the trial license",
                            () => {
                                const t = trialTargets(props.selected_technologies);
                                
                                return t.android || t.ios || t.linux || t.wasm;
                            }
                        ),
                    };
                    
                    if (targets.android) {
                        account_rules["app_name"] = {
                            required: helpers.withMessage(shared_mobile_id ? "The App ID is required" : "The Android application ID is required", required),
                        };
                    }
                    
                    if (targets.ios && !shared_mobile_id) {
                        account_rules["app_name_ios"] = {
                            required: helpers.withMessage("The iOS bundle identifier is required", required),
                        };
                    }
                    
                    if (targets.linux) {
                        account_rules["app_name_linux"] = {
                            required: helpers.withMessage("The Linux app name is required", required),
                            maxLength: helpers.withMessage("Must be 15 characters or fewer", (val) => !val || String(val).trim().length <= 15),
                        };
                    }
                    
                    if (targets.wasm) {
                        account_rules["domain"] = {
                            required: helpers.withMessage("The WASM domain is required", required),
                            validDomain: helpers.withMessage("Enter a valid domain, e.g. example.com", (val) => !val || DOMAIN_PATTERN.test(String(val).trim())),
                        };
                    }
                }
            }
            
            return {
                ...required_rules,
                ...account_rules,
            };
        });
        
        const v$ = useValidate(rules, state);
        const search_country = ref(null);
        const support_countries = general_config.support_countries;
        
        return {
            home_url: HOME_URL,
            state,
            v$,
            support_countries,
            heard_about_us_options: ["Web Search", "Friend(s)", "Youtube", "Social Networks"],
            search_country,
            recaptcha_token: ref(""),
            recaptcha_widget_id: ref(null),
            captcha_wanted: ref(false),
            country_list_used: ref(false),
            ref_ads: ref(""),
            ref_from: ref(""),
            form_submitting: ref(false),
            loadedInIframe: ref(window.self !== window.top),
            recaptcha: useRecaptcha(),
        };
    },
    computed: {
        trial_targets() {
            return trialTargets(this.selected_technologies);
        },
        needs_android() {
            return this.trial_targets.android;
        },
        needs_ios() {
            return this.trial_targets.ios;
        },
        needs_linux() {
            return this.trial_targets.linux;
        },
        wasm_selected() {
            return this.trial_targets.wasm;
        },
        has_trial_targets() {
            const t = this.trial_targets;
            
            return t.android || t.ios || t.linux || t.wasm;
        },
        shared_mobile_id() {
            return this.needs_android && this.needs_ios && !this.state.separate_app_ids;
        },
        generated_message() {
            const lines = [];
            const platforms = this.selected_technologies.filter((t) => PLATFORM_NAMES.includes(t));
            const frameworks = this.selected_technologies.filter((t) => !PLATFORM_NAMES.includes(t));
            const total_barcodes = Object.keys(general_config.barcodes).length;
            
            if (this.state.plan) {
                lines.push("Subscription plan: " + this.state.plan);
            }
            if (platforms.length) {
                lines.push("Platforms: " + platforms.join(", "));
            }
            if (frameworks.length) {
                lines.push("Frameworks: " + frameworks.join(", "));
            }
            if (this.selected_barcodes.length) {
                lines.push(
                    "Symbologies (" + this.selected_barcodes.length + " of " + total_barcodes + "): " +
                    (this.selected_barcodes.length === total_barcodes ? "All" : this.selected_barcodes.join(", "))
                );
            }
            
            return lines.join("<br />");
        },
        passwordHasLength() {
            return !!this.state.password && this.state.password.length >= 8 && this.state.password.length <= 20;
        },
        passwordHasUpper() {
            return /[A-Z]/.test(this.state.password || "");
        },
        passwordHasLower() {
            return /[a-z]/.test(this.state.password || "");
        },
        passwordHasDigitOrSpecial() {
            return /(\d|\W|\s)/.test(this.state.password || "");
        },
        confirmPasswordHasInput() {
            return !!this.state.confirm_password;
        },
        passwordsMatch() {
            return this.confirmPasswordHasInput && !!this.state.password && this.state.password === this.state.confirm_password;
        },
        passwordMismatch() {
            return this.confirmPasswordHasInput && !!this.state.password && this.state.password !== this.state.confirm_password;
        },
        selected_country() {
            const country = this.state.country;

            if (!country) return "";

            return (typeof country === "string" ? country : country.flag2 || "").toUpperCase();
        },
    },
    watch: {
        generated_message: {
            handler(message) {
                this.state.message = message;
            },
            immediate: true,
        },
    },
    methods: {
        submitForm() {
            let self = this;
            
            this.v$.$validate();
            
            if (this.v$.$error) {
                const missing_selection = this.v$.message.$error;
                
                window.barKoder.notify(
                    "error",
                    missing_selection
                        ? "Select at least one Platform, Framework or Symbology."
                        : "Please complete all required fields before submitting the form."
                );
                
                this.$nextTick(() => {
                    const firstError = missing_selection
                        ? document.getElementById("platformsAndFrameworks")
                        : this.$el.querySelector(".q-field.invalid, .q-field .invalid, .q-option-note.invalid");
                    
                    if (firstError) {
                        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                });
                return;
            }
            
            self.form_submitting = true;
            
            let post_params = {
                first_name: self.state.first_name,
                last_name: self.state.last_name,
                email: self.state.email,
                description: self.state.description,
                number_of_apps: self.state.number_of_apps,
                password: self.state.password,
                confirm_password: self.state.confirm_password,
                company: self.state.company,
                country: self.selected_country,
                message: self.state.message,
                number_of_devices: self.state.number_of_devices,
                type_of_app: self.state.type_of_app,
                access_restrictions: self.state.access_restrictions,
                heard_about_us: self.state.heard_about_us,
                app_name: self.state.create_license && self.needs_android ? self.state.app_name.trim() : "",
                app_name_ios: self.state.create_license && self.needs_ios ? (self.shared_mobile_id ? self.state.app_name : self.state.app_name_ios).trim() : "",
                app_name_linux: self.state.create_license && self.needs_linux ? self.state.app_name_linux.trim() : "",
                domain: self.state.create_license && self.wasm_selected ? self.state.domain.trim() : "",
                recaptcha_token: self.state.recaptcha_token,
                ref_ads: self.ref_ads,
                ref_from: self.ref_from,
            };
            
            if (self.state.create_acc) {
                post_params["create_acc"] = 1;
            }
            
            if (self.state.create_license) {
                post_params["create_license"] = 1;
            }
            
            var requestOptions = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(post_params),
            };
            
            if (ENV == "dev") {
                requestOptions["headers"]["authorization"] = "Basic " + btoa(WEB_USERNAME + ":" + WEB_PASS);
            }
            
            fetch(this.home_url + "quotes/submit/new", requestOptions)
            .then(async (response) => {
                let data = null;
                
                try {
                    data = await response.json();
                } catch (e) {
                    data = null;
                }
                
                if (data && data.status) {
                    window.barKoder.notify("success", "Quote submitted!");
                    
                    setTimeout(() => {
                        window.location.href = data.redirect_url;
                    }, 500);
                    return;
                }
                
                // why it was refused, one word (form_error), for Google Ads and GA4
                if (data && window.barKoder && window.barKoder.trackOutcomes) window.barKoder.trackOutcomes(data.tracking || []);
                self.submitFailed(data && data.message ? data.message : "Something went wrong. Please try again.");
            })
            .catch(() => {
                self.submitFailed("We couldn't reach the server. Please check your connection and try again.");
            });
        },
        
        fieldClass(field) {
            const rule = this.v$[field];
            
            return {
                invalid: !!(rule && rule.$error),
                qvalid: !!(rule && !rule.$error && this.state[field]),
            };
        },
        
        submitFailed(message) {
            this.form_submitting = false;
            this.resetRecaptcha();
            window.barKoder.notify("error", message);
        },
        
        resetRecaptcha() {
            this.state.recaptcha_token = "";
            
            if (this.recaptcha_widget_id !== null) {
                this.recaptcha.handleReset(this.recaptcha_widget_id);
            }
        },
        
        queryParamsParse() {
            let queryString = window.location.search,
            urlParams = new URLSearchParams(queryString),
            get_ref_ads = urlParams.get("ref_ads"),
            get_ref_from = urlParams.get("ref_from"),
            devices_num = urlParams.get("devices"),
            type_of_app = urlParams.get("type_of_app"),
            plan = urlParams.get("plan");
            
            if (devices_num) {
                this.state.number_of_apps = 1;
                this.state.number_of_devices = decodeURIComponent(devices_num);
            }
            
            if (type_of_app) {
                this.state.type_of_app = decodeURIComponent(type_of_app);
            }
            
            if (plan) {
                this.state.heard_about_us = "Web Search";
                this.state.plan = decodeURIComponent(plan);
                
                setTimeout(() => {
                    let platformsAndFrameworks = document.getElementById("platformsAndFrameworks");
                    
                    if (platformsAndFrameworks) {
                        window.barKoder.scrollTo(platformsAndFrameworks, -100);
                    }
                }, 500);
            }
            
            if (get_ref_ads) {
                this.ref_ads = get_ref_ads;
            }
            if (get_ref_from) {
                this.ref_from = get_ref_from;
            }
        },
        handleWidgetId: function (widgetId) {
            this.recaptcha_widget_id = widgetId;
        },
        handleErrorCalback: function () {
            this.state.recaptcha_token = "";
        },
        handleExpiredCallback: function () {
            this.state.recaptcha_token = "";
        },
        handleLoadCallback: function (response) {
            this.state.recaptcha_token = response;
        },
    },
    mounted: function () {
        this.queryParamsParse();
        
        var flashdata = {};
        
        const flashdataCollectionDiv = document.getElementsByClassName("frontpage_flashdata");
        
        if (flashdataCollectionDiv && flashdataCollectionDiv.length) {
            let textContent = flashdataCollectionDiv[0].textContent;
            
            if (textContent) flashdata = JSON.parse(textContent);
        }
        
        if (Object.keys(flashdata).length) {
            if (typeof flashdata.validation_errors != "undefined") {
                Object.keys(flashdata.validation_errors).forEach((key) => {
                    window.barKoder.notify("error", flashdata.validation_errors[key]);
                });
            }
            
            const user_data = flashdata.user_data || {};
            
            if (typeof user_data.first_name != "undefined") {
                this.state.first_name = user_data.first_name;
            }
            if (typeof user_data.last_name != "undefined") {
                this.state.last_name = user_data.last_name;
            }
            if (typeof user_data.email != "undefined") {
                this.state.email = user_data.email;
            }
            if (typeof user_data.company != "undefined") {
                this.state.company = user_data.company;
            }
            if (typeof user_data.country != "undefined") {
                const code = String(user_data.country).toLowerCase();
                this.state.country = this.support_countries.find((c) => c.flag2 === code) || "";
            }
            if (typeof user_data.app_name != "undefined") {
                this.state.app_name = user_data.app_name;
            }
            if (typeof user_data.password != "undefined") {
                this.state.password = user_data.password;
            }
            if (typeof user_data.confirm_password != "undefined") {
                this.state.confirm_password = user_data.confirm_password;
            }
            if (typeof user_data.heard_about_us != "undefined") {
                this.state.heard_about_us = user_data.heard_about_us;
            }
        }
    },
};
</script>
